import React, { useState, useEffect } from 'react';
import Input from '../common/Input';
import Button from '../common/Button';
import Modal from '../common/Modal';
import HoleInput from './HoleInput';
import ScorecardUpload from './ScorecardUpload';

export default function CourseForm({ course = null, onSubmit, onCancel, isLoading = false }) {
  const [formData, setFormData] = useState({
    name: '',
    holes: Array.from({ length: 18 }, (_, i) => ({
      holeNumber: i + 1,
      par: null,
      strokeIndex: null
    }))
  });

  const [errors, setErrors] = useState({});
  const [holeErrors, setHoleErrors] = useState({});
  const [showScorecardUpload, setShowScorecardUpload] = useState(false);

  // Populate form if editing (course info only, holes can't be edited in bulk)
  useEffect(() => {
    if (course) {
      setFormData({
        name: course.name || '',
        holes: course.holes || Array.from({ length: 18 }, (_, i) => ({
          holeNumber: i + 1,
          par: null,
          strokeIndex: null
        }))
      });
    }
  }, [course]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleHoleChange = (index, holeData) => {
    setFormData((prev) => {
      const newHoles = [...prev.holes];
      newHoles[index] = holeData;
      return { ...prev, holes: newHoles };
    });

    // Clear errors for this hole
    if (holeErrors[index]) {
      setHoleErrors((prev) => ({ ...prev, [index]: {} }));
    }
  };

  const validate = () => {
    const newErrors = {};
    const newHoleErrors = {};

    // Validate course details
    if (!formData.name.trim()) {
      newErrors.name = 'Course name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Course name must be at least 2 characters';
    }

    // Validate holes
    const strokeIndexes = new Set();
    let hasHoleErrors = false;

    formData.holes.forEach((hole, index) => {
      const errors = {};

      if (!hole.par) {
        errors.par = 'Required';
        hasHoleErrors = true;
      } else if (hole.par < 3 || hole.par > 5) {
        errors.par = 'Must be 3, 4, or 5';
        hasHoleErrors = true;
      }

      if (!hole.strokeIndex) {
        errors.strokeIndex = 'Required';
        hasHoleErrors = true;
      } else if (hole.strokeIndex < 1 || hole.strokeIndex > 18) {
        errors.strokeIndex = 'Must be 1-18';
        hasHoleErrors = true;
      } else if (strokeIndexes.has(hole.strokeIndex)) {
        errors.strokeIndex = 'Duplicate';
        hasHoleErrors = true;
      } else {
        strokeIndexes.add(hole.strokeIndex);
      }

      if (Object.keys(errors).length > 0) {
        newHoleErrors[index] = errors;
      }
    });

    if (hasHoleErrors) {
      newErrors.holes = 'Please fix hole errors above';
    }

    setErrors(newErrors);
    setHoleErrors(newHoleErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate()) {
      onSubmit(formData);
    } else {
      // Scroll to first error
      const firstErrorElement = document.querySelector('.border-error');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Auto-fill stroke indexes (optional helper)
  const autoFillStrokeIndexes = () => {
    setFormData((prev) => ({
      ...prev,
      holes: prev.holes.map((hole, index) => ({
        ...hole,
        strokeIndex: index + 1
      }))
    }));
  };

  // Handle scorecard data extraction
  const handleScorecardData = (data) => {
    setFormData({
      ...formData,
      // Keep existing course name if user already entered one, otherwise use extracted name
      name: formData.name.trim() ? formData.name : (data.name || ''),
      holes: data.holes.map((hole, index) => ({
        holeNumber: hole.holeNumber || index + 1,
        par: hole.par,
        strokeIndex: hole.strokeIndex,
        yardage: hole.yardage || null
      }))
    });
    setShowScorecardUpload(false);

    // Clear any existing errors
    setErrors({});
    setHoleErrors({});
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Global error display */}
      {Object.keys(errors).length > 0 && (
        <div className="bg-red-600 text-white px-4 py-4 mx-4 rounded-lg shadow-lg">
          <p className="font-bold text-lg mb-2">⚠️ Please Fix Errors:</p>
          <ul className="list-disc list-inside space-y-1">
            {Object.entries(errors).map(([key, value]) => (
              <li key={key} className="text-sm">{value}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Course Details */}
      <div className="bg-white px-4 py-4 border-b border-gray-200">
        <Input
          label="Course Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          placeholder="e.g., St Andrews Old Course"
          required
          autoFocus
        />

        {/* Scorecard Upload - Only show when creating new course */}
        {!course && (
          <div className="mt-4 p-4 border-2 border-dashed border-primary rounded-lg">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowScorecardUpload(true)}
              className="w-full"
            >
              📸 Upload Scorecard Photo
            </Button>
            <p className="text-xs text-gray-600 mt-2 text-center">
              Or enter course details manually below
            </p>
          </div>
        )}
      </div>

      {/* Holes Section */}
      <div className="bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-gray-900">
            18 Holes - Par & Stroke Index
          </h3>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={autoFillStrokeIndexes}
            className="text-xs"
          >
            Auto-fill SI (1-18)
          </Button>
        </div>

        {errors.holes && (
          <div className="bg-red-600 text-white px-4 py-3 mb-4">
            {errors.holes}
          </div>
        )}

        <div className="space-y-2">
          {formData.holes.map((hole, index) => (
            <HoleInput
              key={index}
              holeNumber={index + 1}
              hole={hole}
              onChange={handleHoleChange}
              errors={holeErrors[index] || {}}
            />
          ))}
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 bg-white p-4 border-t border-gray-200 mt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="success"
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading ? 'Saving...' : course ? 'Update' : 'Create'}
        </Button>
      </div>

      {/* Scorecard Upload Modal */}
      <Modal
        isOpen={showScorecardUpload}
        onClose={() => setShowScorecardUpload(false)}
        title="Upload Course Scorecard"
        size="lg"
      >
        <ScorecardUpload
          onDataExtracted={handleScorecardData}
          onCancel={() => setShowScorecardUpload(false)}
        />
      </Modal>
    </form>
  );
}
